import * as React from "react";
import { Form, Checkbox, useFormState, useFormApi } from "informed";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { TextBox } from "../../../forms/informedv2/TextBox";
import { MultiSelect } from "../../../forms/informedv2/MultiSelect";
import { TextBoxWithStaticSuffix } from "../../../forms/informedv2/TextBoxWithStaticSuffix";
import { IntegerValidator, MinMaxValidator } from "../../../forms/validation/NumericValidators";
import { ActionButtons } from "../../../misc/ActionButtons";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { useParams, Link } from "react-router-dom";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { ConvertToSelectListItemArray } from "./ParameterSelectList";
import { AuditForm, AuditModal } from "../../../auditmodal/components/AuditModal";
import { StudyEcgSetting } from "../../../misc/api/apiTypes";
import { Required } from "../../../forms/validation/Required";
import { useCookieRedirect } from "../../../misc/CookieRedirect";
import { useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { ISelectListItem } from "../../../misc/AspnetSelectList";

interface EcgSettingsScreenFormState {
    maxRecordings: number;
    recordingDuration: number;
    selectedTestParameters: ISelectListItem[];
    pacemaker: boolean;
    qtcParameter: { Value: string; Text: string };
}

interface EcgSettingsScreenFormProps {
    studyId: string;
    parameters: any[];
}

export const EcgSettingsScreenForm = ({ studyId, parameters }: EcgSettingsScreenFormProps) => {
    const formApi = useFormApi<EcgSettingsScreenFormState>();
    const formState = useFormState<EcgSettingsScreenFormState>();
    const [alertShown, setAlertShown] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const { refreshSettings } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();

    const convertToIdsArray = (params: ISelectListItem[]) => {
        return params.length > 0
            ? parameters.filter(p => params.map(s => s.Value).includes(p.Keyword)).map(p => p.Id)
            : null;
    }

    const onPreAuditSubmit = () => {
        setAlertShown(false);
        setErrorAlert(null);
        setSubmitting(true);
        formApi.validate();

        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setAuditModalVisible(false);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        const ecgSettings: StudyEcgSetting = {
            SelectedTestParameters: convertToIdsArray(state.values.selectedTestParameters),
            MaximumRecordings: state.values.maxRecordings,
            AnalysisQTCParameterKey: parameters.find(p => state.values.qtcParameter.Value === p.Keyword).Id,
            IsPacemakerDetectOn: state.values.pacemaker,
            RecordingDuration: state.values.recordingDuration,
        }

        try {
            const result = await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Ecg.StudyEcgSetting", ecgSettings);

            if (result) {
                setAlertShown(true);
                refreshSettings();
            }
        } catch (ex) {
            setErrorAlert(ex.Message);
        }

        setSubmitting(false);
    }

    const maximumRecordingValidation = (value: number) => {
        return Required(value) || IntegerValidator(value) || MinMaxValidator(value, 1, 3);
    }

    const durationValidation = (value: number) => {
        return Required(value) || IntegerValidator(value) || MinMaxValidator(value, 10, 300);
    }

    const parametersSelectList: ISelectListItem[] = parameters.map(p => {
        return {
            Id: p.Id,
            Value: p.Keyword,
            Text: p.DisplayName + (p.UnitName ? ` (${p.UnitName})` : ''),
            Group: null,
            Selected: false,
            Disabled: false
        };
    });

    const qtcParameters = parameters.filter(p => p.DisplayName.includes("QTc"))
        .map(p => {
            return {
                Value: p.Keyword,
                Text: p.DisplayName + (p.UnitName ? ` (${p.UnitName})` : ''),
            }
        });

    return (
        <>
            <Alert show={alertShown} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies`}>Studies list</Link>?
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">ECG Settings</h2>
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Session Configuration</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Maximum Recordings</label>
                                    <p className="help-block">Configures the maximum number of recordings allowed</p>
                                    <TextBox<number, EcgSettingsScreenFormState> id="maxRecordings" field="maxRecordings" type="number" min="1" max="3" required validate={maximumRecordingValidation} />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Recording Duration</label>
                                    <p className="help-block">Configures the duration of a recording in seconds</p>
                                    <TextBoxWithStaticSuffix id="recordingDuration" field="recordingDuration" type="number" min="10" max="300" required validate={durationValidation} staticpart="seconds" />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-12">
                                    <label>Displayed Test Parameters</label>
                                    <p className="help-block">The test parameters to be displayed on subject testing screens for supported applications</p>
                                    <MultiSelect
                                        id="selectedTestParameters"
                                        field="selectedTestParameters"
                                        options={parametersSelectList}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Analysis QTC Parameter</label>
                                    <p className="help-block">The QTC parameter to be used for analysis</p>
                                    <MultiSelect
                                        id="qtcParameter"
                                        field="qtcParameter"
                                        options={qtcParameters}
                                        validate={Required}
                                        labelKey="Text"
                                        valueKey="Value"
                                    />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Pacemaker Detect</label>
                                    <p className="help-block">Configures whether pacemaker detection is turned on</p>
                                    <Checkbox<boolean, EcgSettingsScreenFormState> field="pacemaker" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <ActionButtons submitting={submitting} onSaveClick={() => onPreAuditSubmit()} automationIdPrefix="ActionsHeader" returnToListRoute="/studies" />
                        </div>
                    </div>
                </div>
                {auditModalVisible ? (
                    <AuditModal
                        header="Approval"
                        isSubmitting={submitting}
                        onClose={() => setAuditModalVisible(false)}
                        onSubmit={(audit) => onSubmit(audit)}
                    />
                ) : null}
            </div>
        </>
    );
}

export const EcgSettings = () => {
    const { id } = useParams<{ id: string }>();
    const [initialValues, setInitialValues] = React.useState<EcgSettingsScreenFormState>();
    const [parameters, setParameters] = React.useState([]);
    const legacyClient = useWebPortalLegacyClient();

    useCookieRedirect(id);

    React.useEffect(() => {
        const getSettings = async () => {
            const ecgParametersResult = await legacyClient.getParametersBySessionType(SessionTypes.Ecg);
            setParameters(ecgParametersResult);

            const ecgSettings = await legacyClient.getStudyEcgSettings(id);

            setInitialValues({
                maxRecordings: ecgSettings.MaximumRecordings,
                recordingDuration: ecgSettings.RecordingDuration,
                pacemaker: ecgSettings.IsPacemakerDetectOn,
                qtcParameter: ConvertToSelectListItemArray([ecgSettings.AnalysisQTCParameterKey], ecgParametersResult)[0],
                selectedTestParameters: ConvertToSelectListItemArray(ecgSettings.SelectedTestParameters, ecgParametersResult),
            });
        }

        getSettings();
    }, [id]);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading ECG Settings..." />;
    }

    const validateFields = (values) => {
        return {
            selectedTestParameters: values.selectedTestParameters === null ? "At least one value must be selected" : undefined,
        }
    }

    return (
        <Form noValidate initialValues={initialValues} validateFields={validateFields}>
            <EcgSettingsScreenForm studyId={id} parameters={parameters} />
        </Form>
    );
}
